import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../inputField';
import { alpha, Box, FormLabel } from '@mui/material';

type Props = {
  submitCode: (code: string) => void;
  codeValid: boolean;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
};

const TwoFactorDigitInput = ({ ...otherProps }) => {
  return (
    <Input
      {...otherProps}
      disableUnderline
      inputProps={{ maxLength: 1 }}
      sx={(theme) => ({
        width: '3rem',
        height: '3rem',
        fontSize: '1rem',
        padding: '12px',
        border: '1px solid ',
        borderColor: 'grey.300',
        borderRadius: '8px',
        gap: '0.75rem',
        '& .Mui-disabled': {
          opacity: '.5 !important'
        },
        '&.Mui-disabled': {
          background: alpha(theme.palette.grey['50'], 0.5)
        },
        '&.Mui-error': {
          border: `1px solid ${theme.palette.error.main}`
        },
        '&.Mui-focused, &:hover': {
          border: `1px solid ${theme.palette.grey['700']}`
        },
        '& > input': {
          textOverflow: 'ellipsis',
          textAlign: 'center'
        }
      })}
    />
  );
};

export const TwoFactorCodeInput: FC<Props> = ({ submitCode, codeValid, error, setError }) => {
  const [timerID, setTimerID] = useState(0);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const { t } = useTranslation();

  const codeRef = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  useEffect(() => {
    const firstElement = codeRef[0].current;
    firstElement && firstElement.focus();
  }, []);

  const handleDigitInputKeyDown = (event: React.KeyboardEvent, index: number) => {
    setError(false);
    let value = event.key;
    if (timerID) {
      clearTimeout(timerID);
      setTimerID(0);
    }

    const navigateLeft = () => {
      const reference = codeRef[index - 1];
      if (reference && reference.current && index > 0) reference.current.focus();
    };

    const navigateRight = () => {
      const reference = codeRef[index + 1];
      if (reference && reference.current && index < 5) reference.current.focus();
    };

    if (event.code === 'Backspace') {
      navigateLeft();
      value = '';
    }

    if (event.code === 'ArrowLeft') navigateLeft();
    if (event.code === 'ArrowRight') navigateRight();

    if (!Number.isNaN(Number(value))) {
      setCode((previousCode) => {
        const newCode = [...previousCode];
        newCode[index] = value;

        const codeString = newCode.join('');
        if (codeString.length === 6) {
          const reference = codeRef[index];
          if (reference && reference.current) reference.current.blur();
          const timeout = setTimeout(() => submitCode(codeString), 500);
          setTimerID(Number(timeout));
        }
        return newCode;
      });

      if (value !== '') navigateRight();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const data = event.clipboardData.getData('Text').trim();
    setCode((previousCode) => {
      const newCode = [...previousCode];

      for (let i = 0; i < newCode.length; i++) {
        if (data[i]) newCode[i] = data[i];
      }

      const codeString = newCode.join('');
      if (codeString.length === 6) {
        (event.target as HTMLInputElement).blur();
        const timeout = setTimeout(() => submitCode(codeString), 500);
        setTimerID(Number(timeout));
      }

      return newCode;
    });
  };

  return (
    <>
      <Box display={'flex'} gap={0.5}>
        {code.map((digit, index) => (
          <TwoFactorDigitInput
            key={index}
            inputRef={codeRef[index]}
            disabled={codeValid}
            onKeyDown={(event: React.KeyboardEvent) => handleDigitInputKeyDown(event, index)}
            onPaste={handlePaste}
            value={Number.isNaN(digit) ? '' : digit}
            error={error}
          />
        ))}
      </Box>

      {error && (
        <FormLabel sx={{ marginTop: '0.5rem!important' }} error>
          {t('account:ERROR_LOGIN_CODE')}
        </FormLabel>
      )}
    </>
  );
};
